// src/hooks/useSSE.ts
import { useState, useEffect, useCallback, useRef } from "react";
import config from "../config.js";
import { useAppDispatch, useAppSelector } from "./storeHook";
import { ActionTypes } from "../store/actionTypes";
import { ChatAction } from "../actions/chatAction";
import { acquireSSE, getOrCreateSSE, destroySSE, releaseSSE } from "../shared/utils/sseSingleton";
import { createSSEManager, generateConnectionId, type SSEConfig, type SSEHandlers } from "../shared/utils/sseUtils";
import {
  extractHumanReviewPayloadFromContent,
  formatExportRequestForDisplay,
  normalizeHumanReviewPayload,
} from "../shared/utils/messageUtils";
import { shouldApplyThinkingProgressUpdate } from "../shared/utils/progressUpdateDisplay";
import { chatApiInstance } from "../apis/chatApi";
import {
  getWebAuthToken,
  getWebAuthTokenMirror,
} from "../utils/authTokenStorage";
import { announceTicketResolverState } from "./useTicketResolverState";

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
export type UseSSEOptions = {
  ticketId?: string | null;
  /** Surface-specific listener for persisted graph-generated thread titles. */
  onThreadName?: (threadName: string) => void;
};

const toNonEmptyString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
};

export const useSSE = (aiThreadId: string | null, userId: string, options?: UseSSEOptions) => {
  const ticketId = options?.ticketId ?? null;
  const onThreadName = options?.onThreadName;
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const messages = useAppSelector((state) => state.chat.messages);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const hasReceivedFirstContentRef = useRef<boolean>(false);
  const sseManagerRef = useRef<any>(null);
  const currentThreadIdRef = useRef<string | null>(aiThreadId);
  const processedCompleteEntryIdsRef = useRef<Set<string>>(new Set());
  const pendingChunkRef = useRef<any | null>(null);
  const chunkFlushRafRef = useRef<number | null>(null);
  const acquiredThreadIdRef = useRef<string | null>(null);
  const streamRecoveryRef = useRef<{
    active: boolean;
    startedAt: number;
    lastEventAt: number;
    messageId: string | null;
    requestId: string | null;
    attempts: number;
    recovering: boolean;
  }>({
    active: false,
    startedAt: 0,
    lastEventAt: 0,
    messageId: null,
    requestId: null,
    attempts: 0,
    recovering: false,
  });
  const STREAM_STALL_MS = 20000;
  const STREAM_RECOVERY_POLL_MS = 5000;
  const STREAM_RECOVERY_MAX_ATTEMPTS = 120;

  const markStreamStarted = useCallback((requestId?: unknown) => {
    const now = Date.now();
    const nextRequestId = toNonEmptyString(requestId);
    const recovery = streamRecoveryRef.current;
    const isNewStream =
      !recovery.active ||
      Boolean(nextRequestId && recovery.requestId && recovery.requestId !== nextRequestId);

    streamRecoveryRef.current.active = true;
    if (isNewStream || !streamRecoveryRef.current.startedAt) {
      streamRecoveryRef.current.startedAt = now;
      streamRecoveryRef.current.messageId = null;
      streamRecoveryRef.current.attempts = 0;
    }
    streamRecoveryRef.current.lastEventAt = now;
    if (nextRequestId) {
      streamRecoveryRef.current.requestId = nextRequestId;
    }
    streamRecoveryRef.current.recovering = false;
  }, []);

  const markStreamActivity = useCallback((messageId?: unknown, requestId?: unknown) => {
    if (!streamRecoveryRef.current.active) {
      markStreamStarted(requestId);
    }
    streamRecoveryRef.current.lastEventAt = Date.now();
    const nextMessageId = toNonEmptyString(messageId);
    const nextRequestId = toNonEmptyString(requestId);
    if (nextMessageId) {
      streamRecoveryRef.current.messageId = nextMessageId;
    }
    if (nextRequestId) {
      streamRecoveryRef.current.requestId = nextRequestId;
    }
  }, [markStreamStarted]);

  const markStreamFinished = useCallback(() => {
    streamRecoveryRef.current.active = false;
    streamRecoveryRef.current.startedAt = 0;
    streamRecoveryRef.current.lastEventAt = 0;
    streamRecoveryRef.current.messageId = null;
    streamRecoveryRef.current.requestId = null;
    streamRecoveryRef.current.attempts = 0;
    streamRecoveryRef.current.recovering = false;
  }, []);

  const getMessageTimestampMs = (msg: any): number => {
    const created = msg?.created_at;
    if (!created) return -1;
    if (typeof created === "string") {
      const t = Date.parse(created);
      return Number.isFinite(t) ? t : -1;
    }
    if (typeof created?.seconds !== "undefined") {
      const seconds = Number(created.seconds || 0);
      const nanos = Number(created.nanos || 0);
      if (Number.isFinite(seconds)) {
        return seconds * 1000 + Math.floor(nanos / 1_000_000);
      }
    }
    if (typeof created?.value?.seconds !== "undefined") {
      const seconds = Number(created.value.seconds || 0);
      const nanos = Number(created.value.nanos || 0);
      if (Number.isFinite(seconds)) {
        return seconds * 1000 + Math.floor(nanos / 1_000_000);
      }
    }
    const millis = Number(created);
    return Number.isFinite(millis) ? millis : -1;
  };

  const tryRecoverStalledStream = useCallback(async () => {
    if (!aiThreadId) return;
    const recovery = streamRecoveryRef.current;
    if (!recovery.active || recovery.recovering) return;
    const idleMs = Date.now() - recovery.lastEventAt;
    if (idleMs < STREAM_STALL_MS) return;

    // A quiet Cortex stream is valid while the SSE socket is healthy. Recovery is
    // only for lost SSE delivery; polling DB during a live socket can race with
    // late ai_chunk/ai_complete events and render the same assistant turn twice.
    if (sseManagerRef.current?.isConnected?.()) {
      recovery.attempts = 0;
      return;
    }

    if (recovery.attempts >= STREAM_RECOVERY_MAX_ATTEMPTS) {
      dispatch({ type: ActionTypes.END_THINKING });
      dispatch({ type: ActionTypes.CHAT_LOADING, payload: false });
      dispatch({
        type: ActionTypes.CHAT_ERROR,
        payload:
          "Connection interrupted during streaming. Reconnected, but this response could not be recovered. Please resend your message.",
      });
      markStreamFinished();
      return;
    }

    recovery.recovering = true;
    recovery.attempts += 1;
    try {
      const response: any = await chatApiInstance.getAiThreadById(aiThreadId);
      const serverMessages = Array.isArray(response?.ai_thread?.messages)
        ? response.ai_thread.messages
        : [];
      const targetMessageId = recovery.messageId ? String(recovery.messageId) : null;
      const thresholdMs = recovery.startedAt > 0 ? recovery.startedAt - 5000 : 0;

      const reversed = [...serverMessages].reverse();
      let recoveredMessage =
        targetMessageId != null
          ? reversed.find(
              (m: any) =>
                m?.user_id === "neo" &&
                String(m?.message_id || "") === targetMessageId &&
                m?.isStreaming !== true
            )
          : null;

      if (!recoveredMessage) {
        recoveredMessage = reversed.find((m: any) => {
          if (m?.user_id !== "neo" || m?.isStreaming === true) return false;
          const ts = getMessageTimestampMs(m);
          if (ts <= 0) return false;
          return thresholdMs <= 0 || ts >= thresholdMs;
        });
      }

      if (recoveredMessage) {
        dispatch({
          type: ActionTypes.ADD_MESSAGE,
          payload: {
            ai_thread_id: aiThreadId,
            user_id: "neo",
            content: recoveredMessage.content ?? "",
            content_type: recoveredMessage.content_type || "MESSAGE",
            message_id: recoveredMessage.message_id || null,
            agent_display_name: recoveredMessage.agent_display_name ?? null,
            created_at: recoveredMessage.created_at
              ? recoveredMessage.created_at
              : { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
            isStreaming: false,
            isComplete: true,
            entry_id: recoveredMessage.entry_id || null,
            requestId: recovery.requestId || null,
          },
        });
        dispatch({ type: ActionTypes.END_THINKING });
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: false });
        chatApiInstance.refreshThreadAuditCache(aiThreadId);
        if (ticketId) chatApiInstance.refreshTicketAuditCache(ticketId);
        markStreamFinished();
      }
    } catch {
      // Ignore recovery errors and keep polling while stream is marked active.
    } finally {
      recovery.recovering = false;
    }
  }, [aiThreadId, ticketId, dispatch, markStreamFinished]);

  const flushPendingChunk = useCallback(() => {
    chunkFlushRafRef.current = null;
    const chunk = pendingChunkRef.current;
    pendingChunkRef.current = null;
    if (!chunk || !aiThreadId) return;

    dispatch({
      type: ActionTypes.ADD_MESSAGE,
      payload: {
        ai_thread_id: aiThreadId,
        user_id: "neo",
        content: chunk.content ?? "",
        content_type: "MESSAGE",
        message_id: chunk.message_id || null,
        agent_display_name: chunk.agent_display_name ?? null,
        requestId: chunk.request_id || null,
        created_at: { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
        isStreaming: true,
        entry_id: chunk.entry_id || null,
        stream_entry_ids: chunk.stream_entry_ids,
      },
    });
  }, [aiThreadId, dispatch]);

  const queueChunkDispatch = useCallback((chunk: any) => {
    if (pendingChunkRef.current) {
      const streamEntryIds = new Set<string>();
      const addEntryId = (entryId: any) => {
        if (entryId != null && String(entryId).trim()) {
          streamEntryIds.add(String(entryId));
        }
      };
      (pendingChunkRef.current.stream_entry_ids || []).forEach(addEntryId);
      addEntryId(pendingChunkRef.current.entry_id);
      (chunk.stream_entry_ids || []).forEach(addEntryId);
      addEntryId(chunk.entry_id);
      pendingChunkRef.current = {
        ...chunk,
        content: `${pendingChunkRef.current.content ?? ""}${chunk.content ?? ""}`,
        stream_entry_ids: Array.from(streamEntryIds),
      };
    } else {
      pendingChunkRef.current = {
        ...chunk,
        stream_entry_ids: chunk.entry_id ? [String(chunk.entry_id)] : chunk.stream_entry_ids,
      };
    }
    if (chunkFlushRafRef.current != null) return;
    chunkFlushRafRef.current = window.requestAnimationFrame(() => {
      flushPendingChunk();
    });
  }, [flushPendingChunk]);

  const connect = useCallback(async () => {
    if (!aiThreadId || !userId) {
      return;
    }

    if (sseManagerRef.current?.isConnected()) {
      setIsConnected(true);
      return;
    }

    setIsConnected(false);
    setConnectionStatus('connecting');
    setError(null);

    const token =
      (await getWebAuthToken().catch(() => null)) ||
      accessToken ||
      getWebAuthTokenMirror();
    if (!token) {
      setError("No authentication token found");
      setConnectionStatus('disconnected');
      return;
    }

    const sseConfig: SSEConfig = {
      baseURL: config.apiUrl || '',
      userId,
      aiThreadId,
      token,
      connectionId: sseManagerRef.current ? undefined : generateConnectionId(),
      withCredentials: true,
    };

    const sseHandlers: SSEHandlers = {
      onConnected: () => {
        if (currentThreadIdRef.current !== aiThreadId) return;
        setIsConnected(true);
        setConnectionStatus('connected');
        setError(null);
        hasReceivedFirstContentRef.current = false;
      },
      onDisconnected: () => {
        if (currentThreadIdRef.current !== aiThreadId) return;
        setIsConnected(false);
        setConnectionStatus('disconnected');
        hasReceivedFirstContentRef.current = false;
      },
      onReconnecting: (attempt) => {
        if (currentThreadIdRef.current !== aiThreadId) return;
        setIsConnected(false);
        setConnectionStatus('reconnecting');
      },
      onError: (errorMessage) => {
        if (currentThreadIdRef.current !== aiThreadId) return;
        setError(errorMessage);
        dispatch({ type: ActionTypes.CHAT_ERROR, payload: errorMessage });
      },
      onStreamStart: (data?: any) => {
        if (data?.automation_capacity === 0 || data?.automation_capacity === 3) {
          dispatch({ type: ActionTypes.SET_THREAD_AUTOMATION, payload: { aiThreadId, capacity: data.automation_capacity } });
        }
        if (data?.resolver_state && aiThreadId) {
          announceTicketResolverState(aiThreadId, {
            resolver_state: data.resolver_state,
            resolver_context: data.resolver_context ?? null,
          });
        }
        hasReceivedFirstContentRef.current = false;
        markStreamStarted(data?.request_id);
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
        dispatch({ type: ActionTypes.START_THINKING });
      },
      onStreamEnd: (data?: any) => {
        if (data?.automation_capacity === 0 || data?.automation_capacity === 3) {
          dispatch({ type: ActionTypes.SET_THREAD_AUTOMATION, payload: { aiThreadId, capacity: data.automation_capacity } });
        }
        markStreamFinished();
        if (data?.cancelled === true) {
          dispatch({ type: ActionTypes.DROP_STREAMING_ASSISTANT });
        } else {
          const lastMessage = messages[messages.length - 1];
          if (lastMessage && lastMessage.user_id === "neo" && lastMessage.isStreaming) {
            dispatch({
              type: ActionTypes.ADD_MESSAGE,
              payload: {
                ...lastMessage,
                isStreaming: false,
              },
            });
          }
        }
        dispatch({ type: ActionTypes.END_THINKING });
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: false });
        chatApiInstance.refreshThreadAuditCache(aiThreadId);
        if (ticketId) chatApiInstance.refreshTicketAuditCache(ticketId);
      },
      onUpdate: (update, data?: any) => {
        markStreamActivity(undefined, data?.request_id);
        // Keep applying progress for the whole active stream. Mid-turn assistant
        // text (e.g. goal-before-tools) must not suppress later connecting /
        // running-script ThinkingIndicator animations.
        if (!shouldApplyThinkingProgressUpdate(update)) {
          return;
        }
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
        dispatch({ type: ActionTypes.START_THINKING });
        dispatch({ type: ActionTypes.HANDLE_STATUS_UPDATE, payload: update });
      },
      onTicketRefresh: () => {
        dispatch({ type: ActionTypes.REFRESH_TICKET_DATA });
      },
      onMessage: (data: any) => {
        if (currentThreadIdRef.current && currentThreadIdRef.current !== aiThreadId) {
          return;
        }
        if (!currentThreadIdRef.current && aiThreadId) {
          currentThreadIdRef.current = aiThreadId;
        }
        
        if (data.type === "thread_automation") {
          dispatch({ type: ActionTypes.SET_THREAD_AUTOMATION, payload: { aiThreadId, capacity: data.automation_capacity } });
          if (data.resolver_state && aiThreadId) {
            announceTicketResolverState(aiThreadId, {
              resolver_state: data.resolver_state,
              resolver_context: data.resolver_context ?? null,
            });
          }
        } else if (data.type === "ai_chunk" || data.type === "stream_chunk") {
          const content = data.content ?? "";
          hasReceivedFirstContentRef.current = true;
          markStreamActivity(data.message_id, data.request_id);
          dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
          queueChunkDispatch({
            content,
            message_id: data.message_id || null,
            request_id: data.request_id || null,
            entry_id: data.entry_id || null,
            agent_display_name: data.agent_display_name ?? null,
          });
        } else if (data.type === "user_message" || (data.type === "user_message_pending" && data.content)) {
          if (data.type === "user_message_pending") {
            hasReceivedFirstContentRef.current = false;
            markStreamStarted(data.request_id);
            dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
            dispatch({ type: ActionTypes.START_THINKING });
          }
          const contentType = data.content_type || "MESSAGE";
          const displayContent =
            contentType === "EXPORT_REQUEST"
              ? formatExportRequestForDisplay(data.content)
              : data.content;
          dispatch({
            type: ActionTypes.ADD_MESSAGE,
            payload: {
              ai_thread_id: aiThreadId,
              user_id: data.userId || data.user_id,
              content: displayContent,
              agent_display_name: data.agent_display_name ?? null,
              content_type: contentType,
              message_id: data.message_id || null,
              pending: data.type === "user_message_pending",
              requestId: data.request_id || null,
              entry_id: data.entry_id || null,
              created_at: data.created_at ? 
                { seconds: `${Math.floor(new Date(data.created_at).getTime() / 1000)}`, nanos: 0 } :
                { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
            },
          });
        } else if (data.type === "message_id_assigned" && data.message_id && data.request_id) {
          dispatch({
            type: ActionTypes.UPDATE_MESSAGE_STATUS,
            payload: {
              request_id: data.request_id,
              message_id: data.message_id,
              pending: false,
            },
          });
        } else if (data.type === "ai_complete") {
          markStreamFinished();
          if (chunkFlushRafRef.current != null) {
            window.cancelAnimationFrame(chunkFlushRafRef.current);
            chunkFlushRafRef.current = null;
          }
          if (pendingChunkRef.current) {
            flushPendingChunk();
          }

          const completeEntryId = data.entry_id;
          if (completeEntryId && processedCompleteEntryIdsRef.current.has(completeEntryId)) {
            return;
          }
          if (completeEntryId) {
            processedCompleteEntryIdsRef.current.add(completeEntryId);
            setTimeout(() => {
              processedCompleteEntryIdsRef.current.delete(completeEntryId);
            }, 10000);
          }
          // A single structured-workflow stream can contain several completed
          // step messages. End the current activity segment so the next status
          // update starts a fresh list for the next workflow step.
          dispatch({ type: ActionTypes.END_THINKING });
          
          const explicitHumanReview = data.human_review
            ? normalizeHumanReviewPayload(data.human_review)
            : null;
          const shouldInterpretAsHumanReview =
            data.content_type === "HUMAN_REVIEW" || Boolean(explicitHumanReview);
          const contentHumanReview = explicitHumanReview
            ? null
            : extractHumanReviewPayloadFromContent(data.content);
          const normalizedHumanReview = explicitHumanReview || contentHumanReview;
          const isHumanReviewComplete =
            shouldInterpretAsHumanReview || Boolean(normalizedHumanReview);
          const completeContent = isHumanReviewComplete ? "" : (data.content || "");
          
          dispatch({
            type: ActionTypes.ADD_MESSAGE,
            payload: {
              ai_thread_id: aiThreadId,
              user_id: "neo",
              content: completeContent,
              agent_display_name: data.agent_display_name ?? null,
              content_type: isHumanReviewComplete
                ? "HUMAN_REVIEW"
                : (data.content_type || "MESSAGE"),
              message_id: data.message_id || null,
              created_at: data.created_at ? 
                { seconds: `${Math.floor(new Date(data.created_at).getTime() / 1000)}`, nanos: 0 } :
                { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
              isStreaming: false,
              isComplete: true,
              entry_id: completeEntryId || null,
              requestId: data.request_id || null,
              ...(normalizedHumanReview && { human_review: normalizedHumanReview }),
            },
          });
        } else if (data.type === "human_review_status" || data.type === "human_review_response") {
          if (!data.system_wait) markStreamFinished();
          const status = (data?.status || "approved").toLowerCase();
          dispatch({ 
            type: ActionTypes.HUMAN_REVIEW_RESPONDED, 
            payload: {
              status,
              pause_ids: data?.pause_ids,
              card_updates: data?.card_updates,
              information_answers: data?.information_answers,
              message_id: data?.message_id || null,
              tool_action: data?.tool_action || null,
              call_id: data?.call_id || null,
              plan_action: data?.plan_action || null,
              plan_id: data?.plan_id || null,
              selected_plan_name: data?.selected_plan_name || null,
              schedule_id: data?.schedule_id || null,
              schedule_title: data?.schedule_title || null,
            },
          });
          chatApiInstance.refreshThreadAuditCache(aiThreadId);
          if (ticketId) chatApiInstance.refreshTicketAuditCache(ticketId);
        } else if (data.type === "thread_deleted") {
          const deletedThreadId = data.ai_thread_id ?? null;
          const isCurrentDeleted =
            deletedThreadId != null &&
            aiThreadId != null &&
            String(deletedThreadId) === String(aiThreadId);
          if (deletedThreadId != null) {
            dispatch({ type: ActionTypes.DELETE_USER_THREAD, payload: deletedThreadId });
          }
          if (isCurrentDeleted) {
            markStreamFinished();
            dispatch({ type: ActionTypes.CLEAR_MESSAGES });
            dispatch({ type: ActionTypes.SET_SELECTED_USER_THREAD_ID, payload: null });
            currentThreadIdRef.current = null;
            try { destroySSE(aiThreadId); } catch {}
          }
          if (userId) {
            try { (dispatch as any)(ChatAction.fetchUserThreads(userId)); } catch {}
          }
        } else if (data.type === "human_review_request") {
          markStreamFinished();
          hasReceivedFirstContentRef.current = true;
          
          const explicitHumanReview = data.human_review
            ? normalizeHumanReviewPayload(data.human_review)
            : null;
          const contentHumanReview = explicitHumanReview
            ? null
            : extractHumanReviewPayloadFromContent(data.content);
          const normalizedHumanReview = explicitHumanReview || contentHumanReview;
          
          dispatch({
            type: ActionTypes.ADD_MESSAGE,
            payload: {
              ai_thread_id: aiThreadId,
              user_id: "neo",
              content: "",
              content_type: data.content_type || "HUMAN_REVIEW",
              agent_display_name: data.agent_display_name ?? null,
              requestId: data.request_id || null,
              message_id: data.message_id || null,
              created_at: data.created_at ? 
                { seconds: `${Math.floor(new Date(data.created_at).getTime() / 1000)}`, nanos: 0 } :
                { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
              isStreaming: false,
              ...(normalizedHumanReview && { human_review: normalizedHumanReview }),
            },
          });
          chatApiInstance.refreshThreadAuditCache(aiThreadId);
          if (ticketId) chatApiInstance.refreshTicketAuditCache(ticketId);
        } else if (data.type === "message_error") {
          markStreamFinished();
          hasReceivedFirstContentRef.current = true;
          if (chunkFlushRafRef.current != null) {
            window.cancelAnimationFrame(chunkFlushRafRef.current);
            chunkFlushRafRef.current = null;
          }
          if (pendingChunkRef.current) {
            flushPendingChunk();
          }
          const errorContent = data.content || "Theo could not finish this message. Please try again.";
          dispatch({
            type: ActionTypes.ADD_MESSAGE,
            payload: {
              ai_thread_id: aiThreadId,
              user_id: "neo",
              content: errorContent,
              content_type: "MESSAGE",
              message_id: data.message_id || null,
              requestId: data.request_id || null,
              entry_id: data.entry_id || null,
              created_at: data.created_at ?
                { seconds: `${Math.floor(new Date(data.created_at).getTime() / 1000)}`, nanos: 0 } :
                { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
              isStreaming: false,
              isComplete: true,
              error: errorContent,
              agent_display_name: data.agent_display_name ?? null,
            },
          });
          dispatch({ type: ActionTypes.END_THINKING });
          dispatch({ type: ActionTypes.CHAT_LOADING, payload: false });
          dispatch({ type: ActionTypes.CHAT_ERROR, payload: errorContent });
          chatApiInstance.refreshThreadAuditCache(aiThreadId);
          if (ticketId) chatApiInstance.refreshTicketAuditCache(ticketId);
        } else if (data.type === "ssh_event") {
          const sshEvent = data.ssh_event || data.payload || null;
          if (!sshEvent || typeof sshEvent !== "object") {
            console.debug("[SSE][ssh_event] Ignored malformed event", data);
            return;
          }
          console.debug("[SSE][ssh_event] Received", sshEvent);
          dispatch({
            type: ActionTypes.ADD_SSH_EVENT,
            payload: { threadId: aiThreadId, event: sshEvent },
          });

          const eventType = String(sshEvent.event_type || "").toUpperCase();
          if (eventType === "SESSION_OPENED") {
            console.debug("[SSE][ssh_event] Session opened -> setting remoteSession active");
            dispatch({
              type: ActionTypes.SET_REMOTE_SESSION,
              payload: {
                threadId: aiThreadId,
                remoteSession: {
                  active: true,
                  sessionId: sshEvent.session_id || null,
                  deviceId: sshEvent.device_id || null,
                  osType: sshEvent.os_type || null,
                  shellType: sshEvent.shell_type || null,
                  status: "connected",
                },
              },
            });
          } else if (eventType === "SESSION_CLOSED" || eventType === "SESSION_ERROR" || eventType === "SESSION_OPEN_FAILED") {
            console.debug("[SSE][ssh_event] Session closed/error -> setting remoteSession inactive", eventType);
            dispatch({
              type: ActionTypes.SET_REMOTE_SESSION,
              payload: {
                threadId: aiThreadId,
                remoteSession: {
                  active: false,
                  sessionId: sshEvent.session_id || null,
                  deviceId: sshEvent.device_id || null,
                  osType: sshEvent.os_type || null,
                  shellType: sshEvent.shell_type || null,
                  status:
                    eventType === "SESSION_CLOSED"
                      ? "closed"
                      : "error",
                },
              },
            });
          }
        } else if (data.type === "computer_use_event") {
          const computerUseEvent = data.computer_use_event || data.payload || null;
          if (!computerUseEvent || typeof computerUseEvent !== "object") {
            console.debug("[SSE][computer_use_event] Ignored malformed event", data);
            return;
          }
          console.debug("[SSE][computer_use_event] Received", computerUseEvent);

          // Every event goes through untouched: which of them changes what is the reducer's
          // business, and it is the one place the streamed and stored vocabularies must agree.
          dispatch({
            type: ActionTypes.APPLY_COMPUTER_USE_EVENT,
            payload: { threadId: aiThreadId, event: computerUseEvent },
          });
        } else if (data.content && data.type !== "user_message") {
          if (data.isStreaming === true) {
            markStreamActivity(data.message_id, data.request_id);
          } else {
            markStreamFinished();
          }
          hasReceivedFirstContentRef.current = true;

          const explicitHumanReview = data.human_review
            ? normalizeHumanReviewPayload(data.human_review)
            : null;
          const shouldInterpretAsHumanReview =
            data.content_type === "HUMAN_REVIEW" || Boolean(explicitHumanReview);
          const contentHumanReview = explicitHumanReview
            ? null
            : extractHumanReviewPayloadFromContent(data.content);
          const normalizedHumanReview = explicitHumanReview || contentHumanReview;
          const isHumanReviewMessage =
            shouldInterpretAsHumanReview || Boolean(normalizedHumanReview);
          
          dispatch({
            type: ActionTypes.ADD_MESSAGE,
            payload: {
              ai_thread_id: aiThreadId,
              user_id: "neo",
              content: isHumanReviewMessage ? "" : data.content,
              content_type: isHumanReviewMessage
                ? "HUMAN_REVIEW"
                : (data.content_type || "MESSAGE"),
              message_id: data.message_id || null,
              requestId: data.request_id || null,
              created_at: data.created_at ? 
                { seconds: `${Math.floor(new Date(data.created_at).getTime() / 1000)}`, nanos: 0 } :
                { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
              isStreaming: data.isStreaming === true,
              entry_id: data.entry_id || null,
              ...(normalizedHumanReview && { human_review: normalizedHumanReview }),
            },
          });
          }
      },
      onHumanReview: (data) => {
        if ((data as any)?.status) {
          const status = String((data as any).status || "approved").toLowerCase();
          dispatch({ 
            type: ActionTypes.HUMAN_REVIEW_RESPONDED, 
            payload: {
              status,
              pause_ids: (data as any).pause_ids,
                card_updates: (data as any).card_updates,
              information_answers: (data as any).information_answers,
              message_id: (data as any).message_id || null,
              tool_action: (data as any).tool_action || null,
              call_id: (data as any).call_id || null,
              plan_action: (data as any).plan_action || null,
              plan_id: (data as any).plan_id || null,
              selected_plan_name: (data as any).selected_plan_name || null,
              schedule_id: (data as any).schedule_id || null,
              schedule_title: (data as any).schedule_title || null,
            },
          });
        }
      },
      onThreadName: (threadName) => {
        dispatch({
          type: ActionTypes.UPDATE_THREAD_NAME,
          payload: { ai_thread_id: aiThreadId, thread_name: threadName },
        });
        onThreadName?.(threadName);
      },
      onPlanProgress: (planProgress) => {
        dispatch({
          type: ActionTypes.SET_PLAN_PROGRESS,
          payload: planProgress,
        });
      },
    };

    const manager =
      acquiredThreadIdRef.current === aiThreadId
        ? getOrCreateSSE(aiThreadId, () => createSSEManager(sseConfig, sseHandlers))
        : acquireSSE(aiThreadId, () => createSSEManager(sseConfig, sseHandlers));
    acquiredThreadIdRef.current = aiThreadId;
    manager.updateHandlers(sseHandlers);
    sseManagerRef.current = manager;
    
    if (!manager.isConnected()) {
      manager.connect();
    } else {
      setIsConnected(true);
    }
  }, [
    aiThreadId,
    userId,
    ticketId,
    onThreadName,
    accessToken,
    dispatch,
    messages,
    queueChunkDispatch,
    flushPendingChunk,
    markStreamStarted,
    markStreamFinished,
    markStreamActivity,
  ]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void tryRecoverStalledStream();
    }, STREAM_RECOVERY_POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [tryRecoverStalledStream]);

  useEffect(() => {
    markStreamFinished();
  }, [aiThreadId, markStreamFinished]);

  useEffect(() => {
    if (!aiThreadId || !userId) {
      if (sseManagerRef.current && acquiredThreadIdRef.current) {
        try { releaseSSE(acquiredThreadIdRef.current); } catch {}
        sseManagerRef.current = null;
      }
      acquiredThreadIdRef.current = null;
      currentThreadIdRef.current = null;
      markStreamFinished();
      setIsConnected(false);
      setConnectionStatus('disconnected');
      setError(null);
      return;
    }

    if (currentThreadIdRef.current && currentThreadIdRef.current !== aiThreadId) {
      if (acquiredThreadIdRef.current) {
        try { releaseSSE(acquiredThreadIdRef.current); } catch {}
      }
      acquiredThreadIdRef.current = null;
      sseManagerRef.current = null;
    }

    currentThreadIdRef.current = aiThreadId;
    void connect();

    const statusPollInterval = setInterval(() => {
      if (sseManagerRef.current && currentThreadIdRef.current === aiThreadId) {
        const manager = sseManagerRef.current;
        const actuallyConnected = manager.isConnected();
        const status = manager.getConnectionStatus?.() as ConnectionStatus | undefined;
        setIsConnected(actuallyConnected);
        if (actuallyConnected) setError(null);
        else {
          setError((e) => (e ? e : "Connection lost"));
          if (status === 'disconnected') manager.connect();
        }
        if (status) setConnectionStatus(status);
      }
    }, 2000);

    return () => {
      clearInterval(statusPollInterval);
      if (chunkFlushRafRef.current != null) {
        window.cancelAnimationFrame(chunkFlushRafRef.current);
        chunkFlushRafRef.current = null;
      }
      pendingChunkRef.current = null;
      if (acquiredThreadIdRef.current) {
        try { releaseSSE(acquiredThreadIdRef.current); } catch {}
      }
      acquiredThreadIdRef.current = null;
      sseManagerRef.current = null;
      currentThreadIdRef.current = null;
      markStreamFinished();
      setIsConnected(false);
      setConnectionStatus('disconnected');
      setError(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiThreadId, userId, markStreamFinished]);

  return { 
    isConnected, 
    connectionStatus,
    error
  };
};
